/**
 * FloatingWindowsProvider — multi-open, non-blocking, draggable window
 * manager for the NDS-1 dashboard.
 *
 * Semantics:
 *   • openWindow(kind, payload) → returns id. Cascades positioning so new
 *     windows don't stack directly on old ones.
 *   • Any number of windows can be open at once.
 *   • Windows never block the main board — no scrim, no backdrop, no blur.
 *   • Each window tracks its own {x, y, w, h, z, minimized}. Clicking a
 *     window bumps its z to the top. Minimized windows collapse into a
 *     bottom dock.
 *   • Persistence: intentionally NONE. Every session is a fresh desktop.
 *     The user asked for an operations console, not a browser tab reload.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const Ctx = createContext(null);

const DEFAULT_SIZE = {
  quickEdit:     { w: 440, h: 560 },
  workbench:     { w: 900, h: 620 },
  brief_intake:  { w: 780, h: 720 },
  new_contact:   { w: 500, h: 560 },
  send_sms:      { w: 500, h: 460 },
  open_module:   { w: 480, h: 320 },
  step_coaching: { w: 560, h: 620 },
  quickForms:    { w: 460, h: 600 },
  cardConfig:    { w: 520, h: 640 },
};

const cascadeStep = 32;

const nextPosition = (index) => {
  if (typeof window === "undefined") return { x: 100, y: 100 };
  const baseX = Math.max(24, Math.round(window.innerWidth * 0.55));
  const baseY = 120;
  const x = Math.min(window.innerWidth - 360, baseX + (index % 6) * cascadeStep);
  const y = Math.min(window.innerHeight - 200, baseY + (index % 6) * cascadeStep);
  return { x, y };
};

export const FloatingWindowsProvider = ({ children }) => {
  const [windows, setWindows] = useState([]);
  const zRef = useRef(50);
  const idRef = useRef(0);
  const openCountRef = useRef(0);

  const bumpZ = () => { zRef.current += 1; return zRef.current; };

  const openWindow = useCallback((kind, payload = {}) => {
    const id = `win-${++idRef.current}`;
    const size = DEFAULT_SIZE[kind] || { w: 480, h: 500 };
    const pos = nextPosition(openCountRef.current++);
    const z = bumpZ();
    setWindows((ws) => [...ws, { id, kind, payload, x: pos.x, y: pos.y, w: size.w, h: size.h, z, minimized: false }]);
    return id;
  }, []);

  const closeWindow = useCallback((id) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const closeByKindAndKey = useCallback((kind, keyFn) => {
    setWindows((ws) => ws.filter((w) => !(w.kind === kind && keyFn(w.payload))));
  }, []);

  const focusWindow = useCallback((id) => {
    const z = bumpZ();
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, z, minimized: false } : w)));
  }, []);

  const updateWindow = useCallback((id, patch) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  }, []);

  const minimizeWindow = useCallback((id) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
  }, []);

  const restoreWindow = useCallback((id) => {
    const z = bumpZ();
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: false, z } : w)));
  }, []);

  const closeAll = useCallback(() => setWindows([]), []);

  const value = useMemo(() => ({
    windows,
    openWindow, closeWindow, closeByKindAndKey,
    focusWindow, updateWindow,
    minimizeWindow, restoreWindow,
    closeAll,
  }), [windows, openWindow, closeWindow, closeByKindAndKey, focusWindow, updateWindow, minimizeWindow, restoreWindow, closeAll]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useFloatingWindows = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useFloatingWindows must be used inside <FloatingWindowsProvider>");
  return v;
};
